<template>
  <div v-if="!!phaseData">
    <v-sheet v-if="loading || error" class="d-flex flex-column align-center justify-center row-gap-2" height="50vh">
      <template v-if="error">
        <h3>Um erro ocorreu ao tentar carregar os dados da tela!</h3>
        <v-btn class="flex-grow-0" large color="primary" @click="loadData" :loading="loading"
          >tentar novamente <v-icon class="ml-2" large>mdi-refresh</v-icon></v-btn
        >
      </template>
      <template v-else>
        <h3>Carregando dados...</h3>
        <v-progress-circular indeterminate />
      </template>
    </v-sheet>
    <div v-else>
      <div v-if="!showForm">
        <v-btn
          block
          @click="!isFinishedAttendance ? (showForm = true) : (showForm = false)"
          :disabled="isFinishedAttendance"
        >
          <v-icon>mdi-plus</v-icon>
          Adicionar Crise
        </v-btn>
      </div>
      <div v-else>
        <field-label label="Período da crise" class="mt-6">
          <v-row>
            <v-col lg="3" md="12" sm="3">
              <v-menu
                ref="pickStartDate"
                v-model="pickStartDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="flare.time.start ? $moment(flare.time.start).format('DD/MM/YYYY') : 'Data inicial'"
                    solo
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="flare.time.start"
                  no-title
                  @change=";(pickStartDate = false), (flare.time.end = null)"
                  locale="pt-br"
                  :max="$moment().subtract(1, 'days').toISOString()"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col lg="3" md="12" sm="3">
              <v-menu
                ref="pickEndDate"
                v-model="pickEndDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="flare.time.end ? $moment(flare.time.end).format('DD/MM/YYYY') : 'Data final'"
                    solo
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="flare.time.end"
                  no-title
                  @change="pickEndDate = false"
                  locale="pt-br"
                  :min="flare.time.start"
                  :max="$moment().toISOString()"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col v-if="totalTime" lg="3" md="12" sm="3">
              <field-label label="Total de dias">
                <h4 class="mx-10">
                  {{ totalTime }}
                </h4>
              </field-label>
            </v-col>
          </v-row>
        </field-label>

        <field-label label="Outros eventos durante a crise" v-if="!attendance.data.summarizedFlow">
          <v-form ref="events">
            <v-row class="my-3">
              <v-col cols="6">
                <div v-for="option in eventOptions" :key="option.key">
                  <v-checkbox
                    :value="option.key"
                    v-model="flare.events.selected"
                    hide-details
                    :label="option.label"
                    class="pa-0 ma-0"
                  />
                </div>
              </v-col>
              <v-col cols="6">
                <v-checkbox
                  value="outros"
                  v-model="flare.events.selected"
                  hide-details
                  label="Outros"
                  class="pa-0 ma-0"
                  @change="cleanTextArea('events')"
                />
                <text-area
                  v-if="flare.events.selected.includes('outros')"
                  :max-length="2000"
                  v-model="flare.events.other"
                  :rules="rules.minlength"
                  :required="flare.events.selected.includes('outros')"
                />
              </v-col>
            </v-row>
          </v-form>
        </field-label>

        <!-- ======================================================================================== -->

        <field-label class="mt-12">
          <radio-options
            v-model="flare.triggers.value"
            clearable
            :options="radioDefaultOptions"
            row
            class="mb-2"
            label="Identifica algum gatilho/desencadeantes de crises?"
          />
          <v-form ref="triggers" v-if="flare.triggers.value">
            <v-row class="my-3">
              <v-col cols="3">
                <div v-for="option in triggerOptions" :key="option.key">
                  <v-checkbox
                    :value="option.key"
                    v-model="flare.triggers.selected"
                    hide-details
                    :label="option.label"
                    class="pa-0 ma-0"
                  />
                </div>
              </v-col>
              <v-col cols="6">
                <v-checkbox
                  value="outros"
                  v-model="flare.triggers.selected"
                  hide-details
                  label="Outros"
                  class="pa-0 ma-0"
                  @change="cleanTextArea('triggers')"
                />
                <text-area
                  v-if="flare.triggers.selected.includes('outros')"
                  :max-length="2000"
                  v-model="flare.triggers.other"
                  :required="flare.triggers.selected.includes('outros')"
                  :rules="rules.minlength"
                />
              </v-col>
            </v-row>
          </v-form>
        </field-label>

        <!-- ======================================================================================== -->
        <template v-if="!attendance.data.summarizedFlow">
          <div v-for="key in sortedCategoryKeys" :key="`medicine-category-${key}`">
            <v-expansion-panels accordion class="mb-3">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <v-row no-gutters>
                    <v-col cols="4">
                      {{ medicinesByCategory[key].category.name }}
                    </v-col>
                  </v-row>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-checkbox
                    v-for="medicine in medicinesByCategory[key].medicines"
                    :key="medicine.id"
                    v-model="flare.medicines"
                    :value="medicine.name"
                    :label="medicine.name"
                  />
                  <v-form class="mt-4" ref="addMedicineForm">
                    <div class="d-flex align-center gap-2" :class="{ 'flex-column': $vuetify.breakpoint.smAndDown }">
                      <text-field
                        class="flex-grow-1"
                        :label="`Adicionar novo medicamento do tipo ${medicinesByCategory[key].category.name} à lista`"
                        placehoder="Nome do medicamento"
                        :max-length="60"
                        :rules="rules.minlength"
                        v-model="newMedicineName"
                      />
                      <v-btn class="mt-1" outlined color="primary" small @click="addMedicine(key)">
                        adicionar medicamento<v-icon small class="ml-2">mdi-plus-circle-outline</v-icon>
                      </v-btn>
                    </div>
                  </v-form>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </template>
        <div class="d-flex justify-end">
          <v-btn color="primary" @click="addFlare" :block="$vuetify.breakpoint.smAndDown ? true : false">
            {{ isEditing ? 'Salvar edição' : 'Adicionar crise' }}
          </v-btn>
        </div>
      </div>
      <field-label v-if="phaseData.flares.length > 0" label="Crises adicionadas:" class="mt-6">
        <div class="d-flex mt-3">
          <v-card
            :elevation="2"
            v-for="(flare, index) in phaseData.flares"
            :key="index"
            :width="$vuetify.breakpoint.smAndDown ? '100%' : '25%'"
            :class="!$vuetify.breakpoint.smAndDown ? 'mr-6' : 'ma-0'"
            @click="openFlareDialog(index)"
            :disabled="isEditing"
          >
            <v-card-title class="primary--text">
              {{ 'Crise #' + (index + 1) }}
            </v-card-title>
            <v-card-subtitle>
              {{
                $moment(flare.time.start).format('DD/MM/YYYY') + ' à ' + $moment(flare.time.end).format('DD/MM/YYYY')
              }}
            </v-card-subtitle>
            <v-card-text> </v-card-text>
          </v-card>
        </div>
      </field-label>
      <field-label class="mt-8" label="Anotações">
        <text-area :readonly="disabled" :max-length="2000" v-model="phaseData.notes" :disabled="isFinishedAttendance" />
      </field-label>
    </div>
    <v-dialog
      v-if="phaseData.flares.length > 0"
      v-model="flareDialog"
      max-width="50%"
      :fullscreen="$vuetify.breakpoint.smAndDown ? true : false"
    >
      <v-card>
        <v-card-title class="primary--text">
          {{ 'Crise #' + (flareToShow + 1) }}
          <v-spacer />
          <v-btn fab small color="primary" @click="editFlare" :disabled="isFinishedAttendance">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle>
          {{
            phaseData.flares[flareToShow].time
              ? $moment(phaseData.flares[flareToShow].time.start).format('DD/MM/YYYY') +
                ' à ' +
                $moment(phaseData.flares[flareToShow].time.end).format('DD/MM/YYYY')
              : null
          }}
        </v-card-subtitle>
        <v-card-text>
          <field-label v-if="!attendance.data.summarizedFlow" label="Motivo principal da crise" class="mt-6">
            <v-chip
              v-for="(item, index) in phaseData.flares[flareToShow].events.selected"
              :key="index"
              class="mr-3 mt-3"
              >{{ item.charAt(0).toUpperCase() + item.slice(1).replaceAll('_', ' ') }}</v-chip
            >
          </field-label>
          <field-label
            v-if="phaseData.flares[flareToShow].events.selected.includes('outros')"
            label="Outro:"
            class="mt-6"
          >
            <v-textarea
              height="fit-content"
              dense
              auto-grow
              readonly
              outlined
              rows="1"
              v-model="phaseData.flares[flareToShow].events.other"
            />
          </field-label>
          <field-label label="Desencadeante da crise" class="mt-6">
            <v-chip
              v-for="(item, index) in phaseData.flares[flareToShow].triggers.selected"
              :key="index"
              class="mr-3 mt-3"
              >{{ item.charAt(0).toUpperCase() + item.slice(1).replaceAll('_', ' ') }}</v-chip
            >
          </field-label>
          <field-label
            v-if="phaseData.flares[flareToShow].triggers.selected.includes('outros')"
            label="Outro:"
            class="mt-6"
          >
            <v-textarea
              height="fit-content"
              dense
              auto-grow
              readonly
              outlined
              rows="1"
              v-model="phaseData.flares[flareToShow].triggers.other"
            />
          </field-label>
          <field-label v-if="!attendance.data.summarizedFlow" label="Medicações utilizadas" class="mt-6">
            <v-chip v-for="(item, index) in phaseData.flares[flareToShow].medicines" :key="index" class="mr-3 mt-3">{{
              item.charAt(0).toUpperCase() + item.slice(1).replaceAll('_', ' ')
            }}</v-chip>
          </field-label>
        </v-card-text>
        <v-card-actions class="justify-end px-8 pb-6">
          <v-spacer></v-spacer>
          <v-btn color="red" text small dense @click="deleteFlareDialog = true" :disabled="isFinishedAttendance">
            excluir
          </v-btn>
          <v-btn color="primary" text small dense @click="flareDialog = false"> fechar </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteFlareDialog" max-width="fit-content">
      <v-card>
        <v-card-title> Tem certeza? </v-card-title>
        <v-card-subtitle class="mt-3"> Excluir essa crise (flare) é irreversível. </v-card-subtitle>
        <v-card-actions>
          <v-spacer />
          <v-btn color="red" text small dense @click="deleteFlare(flareToShow)"> sim </v-btn>
          <v-btn color="primary" text small dense @click="deleteFlareDialog = false"> cancelar </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import BasePhase from '@/components/AttendanceForm/general/phase/BasePhase.vue'
import FieldLabel from '@/components/FieldLabel.vue'
import RadioOptions from '@/components/RadioOptions.vue'
import TextArea from '@/components/TextArea.vue'
import TextField from '@/components/TextField.vue'

import {
  FLARES_OPTIONS,
  FLARES_OPTIONS_SUMMARIZED,
} from '@/components/AttendanceForm/general/constants/flaresOptions.js'
import { STATUS_FINISHED } from '@/constants/attendance'
import { MEDICINE_CATEGORIES } from '@/constants/medicine'

import { v4 as uuidv4 } from 'uuid'
import { createNamespacedHelpers } from 'vuex'
const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')

export default {
  components: { FieldLabel, TextArea, TextField, RadioOptions },
  extends: BasePhase,
  data() {
    return {
      rules: {
        minlength: [(v) => (v && v.length >= 1) || 'O campo não pode estar vazio'],
      },
      deleteFlareDialog: false,
      editFlareDialog: false,
      isEditing: false,
      showForm: false,
      flareDialog: false,
      flareToShow: 0,
      pickStartDate: false,
      pickEndDate: false,
      loading: false,
      newMedicineName: null,
      error: false,
      medicines: [],
      flare: {
        time: {
          start: null,
          end: null,
        },
        events: {
          selected: [],
          other: null,
        },
        triggers: {
          selected: [],
          other: null,
        },
        medicines: [],
        other_events: {
          selected: [],
          other: null,
        },
      },
    }
  },
  computed: {
    ...attendanceGettersMap(['attendanceDiseaseId']),
    ...attendanceGettersMap(['attendance']),
    radioDefaultOptions() {
      return [
        { text: 'Sim', value: true },
        { text: 'Não', value: false },
      ]
    },
    triggerOptions() {
      const triggers = this.attendance?.data?.summarizedFlow
        ? FLARES_OPTIONS_SUMMARIZED.triggers
        : FLARES_OPTIONS.triggers
      return triggers.sort((a, b) => a.label.localeCompare(b.label))
    },
    eventOptions() {
      const events = this.attendance?.data?.summarizedFlow ? FLARES_OPTIONS_SUMMARIZED.events : FLARES_OPTIONS.events
      return events
    },
    totalTime() {
      return this.$moment(this.flare.time.end).diff(this.flare.time.start, 'days')
    },
    isFinishedAttendance() {
      return this.attendance?.status === STATUS_FINISHED.value
    },
    medicinesByCategory() {
      const medicinesByCategory = {}
      for (const medicine of this.medicines[0]) {
        if (!medicinesByCategory[medicine.category.id]) {
          medicinesByCategory[medicine.category.id] = {
            category: medicine.category,
            medicines: [],
          }
        }

        medicinesByCategory[medicine.category.id].medicines.push(medicine)
      }
      return medicinesByCategory
    },
    sortedCategoryKeys() {
      const order = {}
      order[MEDICINE_CATEGORIES.TOPICOS] = 1
      order[MEDICINE_CATEGORIES.SISTEMICOS_TRADICIONAIS] = 2
      order[MEDICINE_CATEGORIES.IMUNOBIOLOGICOS] = 3
      return Object.entries(this.medicinesByCategory)
        .sort((entry1, entry2) => order[entry1[1].category.id] - order[entry2[1].category.id])
        .map((entry) => entry[0])
    },
  },
  methods: {
    getDefaultData() {
      const data = { flares: [] }
      return data
    },
    addFlare() {
      if ((!this.flare.events.selected.length > 0 && !this.attendance.data.summarizedFlow) || !this.totalTime) {
        if (!this.totalTime) return this.$root.error('Tempo total deve ser maior que 1 dia')
        if (!this.flare.events.selected.length > 0 && !this.attendance?.data?.summarizedFlow)
          return this.$root.error('Motivo principal da crise é obrigatório')
      } else if (!this.isEditing) {
        this.phaseData.flares.push({ ...this.flare })
        this.resetFlare()
      } else {
        this.phaseData.flares.splice(this.flareToShow, 1, { ...this.flare })
        this.resetFlare()
        this.isEditing = false
      }
    },
    cleanTextArea(area) {
      if (!this.flare[area].selected.includes('outros')) {
        this.flare[area].other = null
      }
    },
    resetFlare() {
      this.flare = {
        time: {
          start: null,
          end: null,
        },
        events: {
          selected: [],
          other: null,
        },
        triggers: {
          selected: [],
          other: null,
        },
        medicines: {
          antibiotics: [],
          topics: [],
          therapy: [],
        },
        other_events: {
          selected: [],
          other: null,
        },
        other_therapies: null,
        summary: null,
      }
    },
    async loadData() {
      try {
        this.loading = true
        const promises = await Promise.allSettled([this.$api.getMedicinesForDiseaseTreatment(this.attendanceDiseaseId)])
        const medicines = promises[0].value.items.filter((m) => m.category.id !== MEDICINE_CATEGORIES.FOTOTERAPICOS)
        this.medicines.push(medicines)
        this.error = false
      } catch (error) {
        this.$root.error(error)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    addMedicine(categoryId) {
      if (this.$refs.addMedicineForm[0].validate()) {
        const id = uuidv4()
        const name = this.newMedicineName
        const category = this.$util.copy(this.medicinesByCategory[categoryId].category)

        this.medicines[0].push({
          id,
          name,
          category,
        })
        this.flare.medicines.push(this.newMedicineName)
        this.newMedicineName = null
        this.$refs.addMedicineForm.forEach((form) => form.resetValidation())
      }
    },
    openFlareDialog(index) {
      this.flareDialog = true
      this.flareToShow = index
    },
    deleteFlare(id) {
      this.flareDialog = false
      this.deleteFlareDialog = false
      if (!this.isFinishedAttendance) {
        this.flareToShow = 0
        this.$nextTick(() => this.phaseData.flares.splice(id, 1))
      } else {
        this.$root.error('Você não pode fazer isso agora')
      }
    },
    editFlare() {
      this.showForm = true
      this.deleteFlareDialog = false
      this.resetFlare()
      this.flare = { ...this.phaseData.flares[this.flareToShow] }
      this.isEditing = true
    },
  },
  mounted() {
    this.loadData()
  },
}
</script>

<style scoped>
:deep(.v-input__prepend-outer) {
  margin-top: 4px !important;
}
:deep(.v-input__append-outer) {
  margin-top: 4px !important;
}
</style>
